import { Link, useNavigate } from "react-router-dom";
import { mediaUrl } from "@/lib/api";
import { useWishlist } from "@/lib/wishlist";
import { useCart } from "@/lib/cart";
import { Button } from "@/components/ui/button";
import { Heart, ShoppingCart, Trash2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import SeoMeta from "@/components/SeoMeta";
import { useStoreBase } from "@/lib/store";
import { useTranslation } from "react-i18next";
import { trackStoreEvent } from "@/lib/storeAds";

export default function StorefrontWishlist({ config }) {
  const { t } = useTranslation();
  const { slug, base } = useStoreBase();
  const nav = useNavigate();
  const { items, remove } = useWishlist();
  const { add } = useCart();
  const primary = config.branding?.primary_color || "#15803D";

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <SeoMeta endpoint={`/seo/${slug}/meta`} fallback={{ title: `Wishlist · ${config.store_info?.name || slug}` }} />
      <div className="flex items-center gap-3 mb-8">
        <Heart className="w-7 h-7" style={{ color: primary }} fill={primary} />
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-stone-900">{t("store.yourWishlist")}</h1>
      </div>

      {items.length === 0 ? (
        <div className="py-20 text-center">
          <Heart className="w-12 h-12 mx-auto text-stone-300" />
          <div className="mt-4 font-display text-lg font-semibold text-stone-900">{t("store.wishlistEmpty")}</div>
          <p className="text-stone-500 text-sm mt-1">{t("store.wishlistHint")}</p>
          <Link to={`${base}/shop`}><Button className="mt-6 btn-store-primary rounded-full">{t("store.browseProducts")} <ArrowRight className="ml-2 w-4 h-4" /></Button></Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((i) => (
            <div key={i.product_id} className="flex gap-4 p-4 rounded-2xl bg-white border border-stone-200/70" data-testid={`wishlist-item-${i.slug}`}>
              <Link to={`${base}/product/${i.slug}`} className="w-24 h-24 rounded-xl overflow-hidden bg-stone-100 flex-shrink-0">
                {i.image ? <img src={mediaUrl(i.image)} alt={i.name} className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-stone-300 text-2xl font-display">{i.name.slice(0, 1)}</div>}
              </Link>
              <div className="flex-1 min-w-0">
                <Link to={`${base}/product/${i.slug}`} className="font-medium text-stone-900 line-clamp-2 hover:underline">{i.name}</Link>
                <div className="mt-1 flex items-center gap-2">
                  <span className="font-mono-nums font-bold text-stone-900">₹{Number(i.price).toLocaleString("en-IN")}</span>
                  {i.mrp && i.mrp > i.price && <span className="text-xs text-stone-400 line-through font-mono-nums">₹{Number(i.mrp).toLocaleString("en-IN")}</span>}
                </div>
                <div className="mt-3 flex items-center gap-2">
                  <Button size="sm" className="btn-store-primary rounded-full" data-testid={`wishlist-add-${i.slug}`} onClick={() => {
                    if (i.hasVariants) {
                      nav(`${base}/product/${i.slug}?select=variant`);
                      return;
                    }
                    add({ id: i.product_id, name: i.name, slug: i.slug, price: i.price, mrp: i.mrp, images: i.image ? [i.image] : [] }, 1);
                    trackStoreEvent("add_to_cart", { value: Number(i.price || 0), currency: "INR", content_ids: [i.product_id], content_name: i.name });
                    toast.success("Added to cart");
                  }}>
                    <ShoppingCart className="w-4 h-4 mr-1" /> {t("store.addToCart")}
                  </Button>
                  <button className="text-stone-400 hover:text-red-600 p-2" data-testid={`wishlist-remove-${i.slug}`} onClick={() => remove(i.product_id)}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
